import ContactsToolbar from "../../components/ContactsToolbar";
import EmptyState from "../../components/EmptyState";
import useFriendHistoryPaginated from "../../hooks/useFriendHistoryPaginated";
import { ChevronLeft, ChevronRight, History } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../../components/UI/Avatar";
import { cn } from "../../../../utils/utils";

const STATUS_STYLE = {
  sent: { label: "Đã gửi", className: "bg-blue-50 text-blue-700" },
  received: { label: "Đã nhận", className: "bg-amber-50 text-amber-700" },
  accepted: {
    label: "Đã chấp nhận",
    className: "bg-emerald-50 text-emerald-700",
  },
  removed: { label: "Đã huỷ kết bạn", className: "bg-gray-100 text-gray-600" },
  cancelled: { label: "Đã huỷ", className: "bg-gray-100 text-gray-600" },
};

const DIRECTION_LABEL = {
  sent: "Gửi đi",
  received: "Nhận được",
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Hiển thị dãy số trang thu gọn: 1 … 4 5 6 … 20
 */
const buildPageList = (current, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push("…");
  pages.push(totalPages);
  return pages;
};

const Pagination = ({
  page,
  totalPages,
  total,
  pageSize,
  hasPrev,
  hasNext,
  goToPage,
}) => {
  if (totalPages <= 1 && total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pageList = buildPageList(page, totalPages);
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 bg-white">
      <div className="text-[12px] text-gray-500">
        Hiển thị <span className="text-gray-700 font-medium">{from}</span>–
        <span className="text-gray-700 font-medium">{to}</span> trong{" "}
        <span className="text-gray-700 font-medium">{total}</span> sự kiện
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => goToPage(page - 1)}
          disabled={!hasPrev}
          className={cn(
            "h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50",
            !hasPrev && "opacity-40 cursor-not-allowed",
          )}
          aria-label="Trang trước"
        >
          <ChevronLeft size={14} />
        </button>
        {pageList.map((p, idx) => {
          if (p === "…") {
            return (
              <span
                key={`dots-${idx}`}
                className="px-1 text-[12px] text-gray-400"
              >
                …
              </span>
            );
          }
          const active = p === page;
          return (
            <button
              key={p}
              onClick={() => goToPage(p)}
              className={cn(
                "h-8 min-w-[32px] px-2 inline-flex items-center justify-center rounded-md text-[12px] font-medium transition-colors",
                active
                  ? "bg-indigo-600 text-white"
                  : "border border-gray-200 text-gray-700 hover:bg-gray-50",
              )}
            >
              {p}
            </button>
          );
        })}
        <button
          onClick={() => goToPage(page + 1)}
          disabled={!hasNext}
          className={cn(
            "h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50",
            !hasNext && "opacity-40 cursor-not-allowed",
          )}
          aria-label="Trang sau"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

const FriendHistoryTab = () => {
  const {
    items,
    total,
    totalPages,
    page,
    pageSize,
    loading,
    reload,
    goToPage,
    hasPrev,
    hasNext,
  } = useFriendHistoryPaginated({ pageSize: 50 });

  return (
    <div className="flex flex-col h-full">
      <ContactsToolbar
        title="Lịch sử kết bạn"
        subtitle={`${total} sự kiện · Trang ${page}/${totalPages}`}
        onReload={reload}
        loading={loading}
        showFilter={false}
      />
      <div className="flex-1 overflow-auto">
        {!loading && items.length === 0 ? (
          <EmptyState
            icon={History}
            title="Chưa có lịch sử"
            description="Các sự kiện kết bạn / huỷ kết bạn sẽ được lưu tại đây."
          />
        ) : (
          <table className="min-w-full text-[13px]">
            <thead className="bg-gray-50 sticky top-0 z-10 text-gray-600">
              <tr className="border-b border-gray-200">
                <th className="text-left px-4 py-2 font-medium">Liên hệ</th>
                <th className="text-left px-4 py-2 font-medium">Chiều</th>
                <th className="text-left px-4 py-2 font-medium">Trạng thái</th>
                <th className="text-left px-4 py-2 font-medium">Nhân viên</th>
                <th className="text-left px-4 py-2 font-medium">Tài khoản</th>
                <th className="text-left px-4 py-2 font-medium">Thời gian</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((r) => {
                const p = r.partner || {};
                const statusConf = STATUS_STYLE[r.status] || {
                  label: r.status,
                  className: "bg-gray-100 text-gray-600",
                };
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarImage src={p.avatar} alt={p.name} />
                          <AvatarFallback className="text-[11px]">
                            {(p.name || r.partnerUid || "?").charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate max-w-[200px]">
                            {p.name ||
                              p.zaloName ||
                              r.partnerUid ||
                              "(Không tên)"}
                          </div>
                          {p.phone && (
                            <div className="text-[11px] text-gray-500">
                              {p.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">
                      {DIRECTION_LABEL[r.direction] || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] ${statusConf.className}`}
                      >
                        {statusConf.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">
                      {r.staff?.name || (
                        <span className="text-gray-400 italic">
                          Tự động / Bot
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 truncate max-w-[160px]">
                      {r.shop?.name}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                      {formatDateTime(r.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        hasPrev={hasPrev}
        hasNext={hasNext}
        goToPage={goToPage}
      />
    </div>
  );
};

export default FriendHistoryTab;
